"use client";

import { useState } from "react";
import {
  ArrowRight,
  ArrowUp,
  Calendar,
  ChevronLeft,
  ChevronRight,
  FileText,
  Inbox,
  LifeBuoy,
  PanelLeft,
  UserRound,
  UsersRound,
} from "lucide-react";
import Link from "next/link";
import { LogoText } from "~/components/logo";
import { SidebarBrandMark } from "~/components/sidebar-brand-mark";
import { INBOX_PRIMARY_ROW_CLASS } from "~/components/chat/components/inbox-layout-classes";
import { useResolvedMediaQuery } from "~/components/patterns/stepper/use-media-query";
import { TijanaAvatar } from "~/components/tijana/avatar";
import { TijanaMessageBubble } from "~/components/tijana/message-bubble";
import { Iphone } from "~/components/ui/iphone";
import { cn } from "~/lib/utils";

type ConversationScene = "boundaries" | "overthinking" | "relationship";
type PreviewScene = ConversationScene | "handoff";

const SCENES: { key: PreviewScene; label: string }[] = [
  { key: "boundaries", label: "Granice" },
  { key: "overthinking", label: "Nemir" },
  { key: "relationship", label: "Veza" },
  { key: "handoff", label: "Put do terapeuta" },
];

const CONVERSATION_TURNS: Record<
  ConversationScene,
  { isMine: boolean; text: string }[]
> = {
  boundaries: [
    {
      isMine: true,
      text: "Opet sam pristala na nešto za šta nemam vremena.",
    },
    {
      isMine: false,
      text: "Šta ti je bilo teže: da kažeš „ne” ili da preuzmeš još jednu obavezu?",
    },
    {
      isMine: true,
      text: "Da kažem ne. Odmah mislim da ću nekoga razočarati.",
    },
    {
      isMine: false,
      text: "Šta bi za tebe značilo da je neko razočaran, čak i kada imaš dobar razlog da odbiješ?",
    },
  ],
  overthinking: [
    {
      isMine: true,
      text: "Čim legnem, krenem da vrtim sve što može da pođe po zlu.",
    },
    {
      isMine: false,
      text: "Da li te više drži ono što se već desilo ili ono što bi moglo da se desi sutra?",
    },
    {
      isMine: true,
      text: "Sutra. U glavi unapred prolazim svaki loš scenario.",
    },
    {
      isMine: false,
      text: "Koji scenario ti trenutno deluje najverovatnije, a koji je samo najglasniji?",
    },
  ],
  relationship: [
    {
      isMine: true,
      text: "Posvađali smo se i sad ne znam da li preterujem.",
    },
    {
      isMine: false,
      text: "Šta je tačno rekao, a šta si ti iz toga zaključila?",
    },
    {
      isMine: true,
      text: "Rekao je da mu treba prostora. Ja sam čula da mu više nije stalo.",
    },
    {
      isMine: false,
      text: "Šta bi se promenilo ako prvo odvojiš njegove reči od onoga čega se plašiš?",
    },
  ],
};

// Use presentation primitives only. Mounting the real app shell/header/chat
// here would load private account queries, billing state and chat transport.
function PreviewSidebar() {
  const navigation = [
    { label: "Razgovori", icon: Inbox },
    { label: "Izveštaji", icon: FileText },
    { label: "Podrška", icon: LifeBuoy },
    { label: "Kalendar", icon: Calendar },
  ];

  return (
    <div
      aria-hidden="true"
      className="bg-sidebar text-sidebar-foreground hidden w-48 shrink-0 flex-col border-r lg:flex"
    >
      <div
        className={cn(INBOX_PRIMARY_ROW_CLASS, "flex items-center gap-1 px-3")}
      >
        <SidebarBrandMark />
        <div>
          <LogoText surface="light" className="text-sm" />
          <p className="text-muted-foreground mt-1 text-[11px]">Klijent</p>
        </div>
      </div>
      <div className="flex h-16 items-center gap-2 border-b px-4 text-sm">
        <Calendar className="text-brand size-4" />
        Zakaži novu sesiju
      </div>
      <div className="space-y-1 p-2">
        {navigation.map(({ label, icon: Icon }, index) => (
          <div
            key={label}
            className={cn(
              "flex items-center gap-2 rounded-md px-3 py-2 text-sm",
              index === 0 && "bg-sidebar-accent font-medium",
            )}
          >
            <Icon className="size-4" />
            {label}
          </div>
        ))}
      </div>
      <div className="mt-auto flex items-center gap-2 border-t p-4 text-sm">
        <UserRound className="bg-secondary size-7 rounded-full p-1.5" />
        Tvoj nalog
      </div>
    </div>
  );
}

function PreviewChat({ scene }: { scene: PreviewScene }) {
  return (
    <div className="bg-background flex min-h-0 min-w-0 flex-1 flex-col">
      <div
        className={cn(
          INBOX_PRIMARY_ROW_CLASS,
          "flex shrink-0 items-center gap-3 px-3 sm:px-4",
        )}
      >
        <ChevronLeft className="size-5 md:hidden" aria-hidden="true" />
        <TijanaAvatar size={40} />
        <div>
          <p className="text-[15px] leading-tight font-semibold">Tijana</p>
          <p className="text-muted-foreground mt-0.5 text-xs">
            AI podrška · uvek dostupna
          </p>
        </div>
      </div>

      <div
        key={scene}
        role="region"
        aria-label="Primer razgovora sa Tijanom"
        tabIndex={0}
        className="focus-visible:ring-brand min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-5 focus-visible:ring-2 focus-visible:outline-hidden focus-visible:ring-inset md:px-6"
      >
        <div className="mx-auto max-w-2xl space-y-4">
          <p className="text-muted-foreground mb-5 text-center text-xs">
            Danas
          </p>
          {scene === "handoff" ? (
            <>
              <TijanaMessageBubble isMine>
                Ovo mi se stalno ponavlja. Želim da pričam sa terapeutom.
              </TijanaMessageBubble>
              <TijanaMessageBubble>
                Možemo da pogledamo terapeute koji rade sa granicama i odnosima
                na poslu. Pre zakazivanja vidiš profil, cenu i slobodne termine.
              </TijanaMessageBubble>
              <Link
                href="/terapeuti"
                className="bg-card hover:bg-secondary focus-visible:ring-brand ml-10 flex items-center gap-3 rounded-lg border p-4 transition-colors focus-visible:ring-2 focus-visible:outline-hidden"
              >
                <UsersRound
                  className="text-brand size-5 shrink-0"
                  aria-hidden="true"
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold">
                    Pogledaj terapeute
                  </span>
                  <span className="text-muted-foreground mt-1 block text-xs">
                    Profili, cene i slobodni termini
                  </span>
                </span>
                <ArrowRight className="size-4 shrink-0" aria-hidden="true" />
              </Link>
            </>
          ) : (
            CONVERSATION_TURNS[scene].map((turn) => (
              <TijanaMessageBubble key={turn.text} isMine={turn.isMine}>
                {turn.text}
              </TijanaMessageBubble>
            ))
          )}
        </div>
      </div>

      <div className="shrink-0 border-t px-3 pt-2 pb-3 md:px-6 md:pb-4">
        <p className="text-muted-foreground mb-2 text-center text-[11px] leading-normal">
          Tijana je AI, ne prava osoba i ne zamena za terapiju.
        </p>
        {/* Illustration, not a disabled live form or a data-collection field. */}
        <div
          aria-hidden="true"
          className="border-input bg-background rounded-xl border p-3"
        >
          <span className="text-muted-foreground text-sm">
            Napiši poruku...
          </span>
          <div className="mt-3 flex justify-end">
            <span className="bg-secondary text-muted-foreground flex size-7 items-center justify-center rounded-full">
              <ArrowUp className="size-4" />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewApp({
  scene,
  mobile,
}: {
  scene: PreviewScene;
  mobile: boolean;
}) {
  return (
    <div
      className={cn(
        "bg-background text-foreground flex h-full min-h-0 text-left",
        mobile && "pt-10 pb-3",
      )}
    >
      {!mobile && <PreviewSidebar />}
      <div className="flex min-w-0 flex-1 flex-col">
        {!mobile && (
          <div
            className={cn(
              INBOX_PRIMARY_ROW_CLASS,
              "flex shrink-0 items-center gap-3 px-4 text-sm",
            )}
          >
            <PanelLeft
              className="text-muted-foreground size-4"
              aria-hidden="true"
            />
            <span className="h-4 border-l" aria-hidden="true" />
            <span className="text-muted-foreground">Razgovori</span>
            <ChevronRight
              className="text-muted-foreground size-3"
              aria-hidden="true"
            />
            <span>Tijana</span>
          </div>
        )}
        <div className="flex min-h-0 flex-1">
          {!mobile && (
            <div
              aria-hidden="true"
              className="w-44 shrink-0 border-r p-3 lg:w-52"
            >
              <div className="border-brand/25 bg-brand/10 flex items-center gap-2 rounded-lg border px-3 py-3">
                <TijanaAvatar size={32} />
                <div>
                  <p className="text-sm font-semibold">Tijana</p>
                  <p className="text-muted-foreground mt-0.5 text-xs">
                    AI podrška
                  </p>
                </div>
              </div>
              <div className="border-input mt-4 rounded-full border px-2 py-2 text-center text-xs">
                Izaberi svog terapeuta
              </div>
            </div>
          )}
          <PreviewChat scene={scene} />
        </div>
      </div>
    </div>
  );
}

export function TijanaAppPreview() {
  const [scene, setScene] = useState<PreviewScene>("boundaries");
  const { value: isDesktop, isResolved } =
    useResolvedMediaQuery("(min-width: 768px)");

  return (
    <figure aria-label="Prikaz Tijane u aplikaciji">
      <figcaption className="mb-5 flex flex-col items-center justify-between gap-4 text-sm md:flex-row">
        <div className="text-center md:text-left">
          <p className="text-editorial-paper-contrast font-semibold">
            Ovako izgleda razgovor
          </p>
          <p className="text-editorial-paper-contrast/75 mt-1 text-xs">
            Ilustracija, ne pravi razgovor
          </p>
        </div>
        <div
          className="border-editorial-paper-contrast/40 inline-flex max-w-full rounded-full border p-1"
          role="group"
          aria-label="Izaberi primer"
        >
          {SCENES.map((item) => (
            <button
              key={item.key}
              type="button"
              aria-pressed={scene === item.key}
              onClick={() => setScene(item.key)}
              className={cn(
                "focus-visible:ring-offset-brand-forest min-h-10 rounded-full px-3 text-xs font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:outline-hidden sm:px-4 sm:text-sm",
                scene === item.key
                  ? "bg-editorial-paper-contrast text-brand-forest"
                  : "text-editorial-paper-contrast hover:bg-white/10",
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      </figcaption>

      {!isResolved ? (
        <div
          className="mx-auto aspect-433/882 w-full max-w-84 md:aspect-auto md:h-160 md:max-w-none"
          aria-hidden="true"
        />
      ) : isDesktop ? (
        <div className="border-brand-forest/35 bg-editorial-paper overflow-hidden rounded-lg border shadow-[0_24px_55px_rgba(0,0,0,0.16)]">
          <div
            className="border-brand-forest/15 flex h-10 items-center border-b px-4"
            aria-hidden="true"
          >
            <div className="flex gap-1.5">
              {[0, 1, 2].map((dot) => (
                <span
                  key={dot}
                  className="bg-brand-forest/20 size-2.5 rounded-full"
                />
              ))}
            </div>
            <span className="border-brand-forest/10 bg-background text-muted-foreground mx-auto rounded-md border px-8 py-1 text-xs lg:px-20">
              resimoprobleme.com/app/inbox/tijana
            </span>
            <span className="w-12" />
          </div>
          <div className="h-150">
            <PreviewApp scene={scene} mobile={false} />
          </div>
        </div>
      ) : (
        <div className="mx-auto w-full max-w-84">
          <Iphone screenClassName="bg-background">
            <PreviewApp scene={scene} mobile />
          </Iphone>
        </div>
      )}
    </figure>
  );
}
